import type { ApiEndpoint, Permission, RiskSeverity } from "@/lib/arch-parser";

export interface PermissionGroup {
  permission: Permission;
  endpoints: ApiEndpoint[];
}

export interface GroupedApiSurface {
  groups: PermissionGroup[];
  ungated: ApiEndpoint[];
}

const SEVERITY_RANK: Record<RiskSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

// Group endpoints under the permission that gates them, ordered severity-first
// (critical -> warning -> info) with document order preserved within a tier.
// An endpoint whose `permission` is empty OR references a scope with no matching
// Permission record falls into `ungated` — a band can't render without a
// Permission record, and the v4 parser already warns on unknown refs. A
// permission that gates no endpoints still appears as a group with an empty list.
export function groupEndpointsByPermission(
  permissions: Permission[],
  endpoints: ApiEndpoint[],
): GroupedApiSurface {
  const scopes = new Set(permissions.map((p) => p.scope));
  const byScope = new Map<string, ApiEndpoint[]>();
  const ungated: ApiEndpoint[] = [];

  for (const endpoint of endpoints) {
    if (endpoint.permission && scopes.has(endpoint.permission)) {
      const list = byScope.get(endpoint.permission);
      if (list) {
        list.push(endpoint);
      } else {
        byScope.set(endpoint.permission, [endpoint]);
      }
    } else {
      ungated.push(endpoint);
    }
  }

  const groups = permissions
    .map((permission, index) => ({ permission, index }))
    .sort((a, b) => {
      const ra = SEVERITY_RANK[a.permission.severity];
      const rb = SEVERITY_RANK[b.permission.severity];
      if (ra !== rb) return ra - rb;
      return a.index - b.index;
    })
    .map(({ permission }) => ({
      permission,
      endpoints: byScope.get(permission.scope) ?? [],
    }));

  return { groups, ungated };
}
